<template>
  <nav class="sidebar-nav">
    <ul class="nav">
      <template v-for="(item, index) in navItems">
        <!-- Title items with collapse functionality -->
        <li
          v-if="item.title"
          :key="'title-' + index"
          class="nav-title"
          :class="{ 'premium-title': item.class === 'premium-title' }"
          @click="toggleSection(index)"
        >
          <i
            class="collapse-icon fas"
            :class="
              isSectionCollapsed(index) ? 'fa-chevron-right' : 'fa-chevron-down'
            "
          ></i>
          {{ item.name }}
        </li>

        <!-- Regular nav items (shown/hidden based on section collapse state) -->
        <li
          v-else-if="!isItemHidden(index)"
          :key="'item-' + index"
          class="nav-item"
          :class="{ 'premium-title': item.class === 'premium-title' }"
        >
          <router-link
            :to="item.url"
            class="nav-link"
            active-class="router-link-exact-active open active"
          >
            <i class="nav-icon" :class="item.icon"></i>
            {{ item.name }}
            <span
              v-if="item.badge && item.badge.text === 'New'"
              class="badge badge-new"
            >
              {{ item.badge.text }}
            </span>
            <span v-else-if="item.badge" class="badge badge-primary">
              {{ item.badge.text }}
            </span>
          </router-link>
        </li>
      </template>
    </ul>
  </nav>
</template>

<script>
export default {
  name: "CustomSidebarNav",
  props: {
    navItems: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      collapsedSections: {}, // Track which sections are collapsed
      storageKey: "sidebar-collapse-state", // Single storage key for all guilds
    };
  },
  created() {
    this.initializeSections();
  },
  mounted() {
    // Prevent scroll events from bubbling to parent when sidebar can't scroll further
    const sidebarNav = this.$el;
    sidebarNav.addEventListener("wheel", this.preventParentScroll, {
      passive: false,
    });
    sidebarNav.addEventListener("touchmove", this.preventParentScrollTouch, {
      passive: false,
    });
  },
  beforeDestroy() {
    // Clean up event listener
    const sidebarNav = this.$el;
    sidebarNav.removeEventListener("wheel", this.preventParentScroll);
    sidebarNav.removeEventListener("touchmove", this.preventParentScrollTouch);
  },
  methods: {
    initializeSections() {
      // Load saved state from localStorage
      const savedState = this.loadCollapsedState();

      const titleIndices = [];
      this.navItems.forEach((item, index) => {
        if (item.title) {
          titleIndices.push(index);
          this.$set(this.collapsedSections, index, true);
        }
      });

      // Only one section can be expanded at a time.
      // Priority: active route > saved state > all collapsed
      let expandedIndex = titleIndices.find((i) =>
        this.sectionHasActiveRoute(i),
      );

      if (expandedIndex === undefined && savedState) {
        expandedIndex = titleIndices.find(
          (i) => savedState[this.navItems[i].name] === false,
        );
      }

      if (expandedIndex !== undefined) {
        this.$set(this.collapsedSections, expandedIndex, false);
      }
    },

    toggleSection(titleIndex) {
      const wasCollapsed = this.collapsedSections[titleIndex] !== false;

      if (wasCollapsed) {
        // Collapse all other sections, expand this one (accordion)
        this.navItems.forEach((item, index) => {
          if (item.title) {
            this.$set(this.collapsedSections, index, index !== titleIndex);
          }
        });
      } else {
        // Collapsing the currently open section
        this.$set(this.collapsedSections, titleIndex, true);
      }

      // Save to localStorage
      this.saveCollapsedState();
    },

    saveCollapsedState() {
      const state = {};
      this.navItems.forEach((item, index) => {
        if (item.title) {
          state[item.name] = this.collapsedSections[index];
        }
      });
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(state));
      } catch (error) {
        console.error("Error saving collapse state:", error);
      }
    },

    loadCollapsedState() {
      try {
        const saved = localStorage.getItem(this.storageKey);
        return saved ? JSON.parse(saved) : null;
      } catch (error) {
        console.error("Error loading collapse state:", error);
        return null;
      }
    },

    sectionHasActiveRoute(titleIndex) {
      const currentPath = this.$route.path;

      // Check if any items after this title match current route
      for (let i = titleIndex + 1; i < this.navItems.length; i++) {
        if (this.navItems[i].title) break; // Stop at next title

        if (this.navItems[i].url) {
          // Remove './' prefix and check if current path ends with this route
          const routePath = this.navItems[i].url.replace("./", "");
          if (currentPath.endsWith(routePath)) {
            return true;
          }
        }
      }
      return false;
    },

    isSectionCollapsed(titleIndex) {
      return this.collapsedSections[titleIndex] === true;
    },

    isItemHidden(itemIndex) {
      // Find the most recent title before this item
      let lastTitleIndex = -1;
      for (let i = itemIndex - 1; i >= 0; i--) {
        if (this.navItems[i].title) {
          lastTitleIndex = i;
          break;
        }
      }

      // If this item belongs to a collapsed section, hide it
      if (lastTitleIndex >= 0 && this.isSectionCollapsed(lastTitleIndex)) {
        return true;
      }

      return false;
    },

    preventParentScroll(e) {
      // Prevent default browser scroll behavior and stop event bubbling
      e.preventDefault();
      e.stopPropagation();

      // Manually handle the scroll within the sidebar
      const element = e.currentTarget;
      element.scrollTop += e.deltaY;
    },

    preventParentScrollTouch(e) {
      // For touch devices, we need to prevent bubbling but allow natural touch scrolling
      // The sidebar's overflow: scroll will handle the actual scrolling
      e.stopPropagation();
    },
  },
};
</script>

<style scoped>
/* Core sidebar navigation styles */
.sidebar-nav {
  position: relative;
  flex: 1;
  overflow-y: scroll;
  overflow-x: hidden;
}

/* Custom scrollbar styling */
.sidebar-nav::-webkit-scrollbar {
  width: 8px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

.nav {
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  list-style: none;
}

/* Nav title (section headers like SETTINGS, MODERATION, etc.) */
.nav-title {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  margin-top: 1rem;
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  list-style: none;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;
}

.nav-title:hover {
  color: #8f9ba6;
}

.nav-title:first-child {
  margin-top: 0;
}

/* Premium nav title styling */
.nav-title.premium-title {
  color: #f8cb00;
}

.nav-title.premium-title:hover {
  color: #ffd700;
}

/* Premium title styling for section headers */
.nav-title.premium-title {
  color: #f8cb00;
}

.nav-title.premium-title:hover {
  color: #ffd700;
}

/* Collapse icon for section titles */
.collapse-icon {
  margin-right: 0.5rem;
  font-size: 10px;
  transition: transform 0.2s ease;
  min-width: 12px;
}

/* Nav item container */
.nav-item {
  position: relative;
  list-style: none;
  transition: all 0.3s ease;
}

/* Nav link */
.nav-link {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: #ffffff;
  font-size: 14px;
  font-weight: 400;
  text-decoration: none;
  transition:
    background 0.3s,
    color 0.3s;
  border-left: 3px solid transparent;
}

.nav-link:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  text-decoration: none;
}

/* Nav icon */
.nav-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 0.75rem;
  color: #73818f;
  font-size: 14px;
  flex-shrink: 0;
}

/* Active state styling */
.router-link-exact-active,
.router-link-exact-active.open.active {
  background: #343b41;
  color: #fff;
  border-left-color: #20a8d8;
}

.router-link-exact-active .nav-icon {
  color: #20a8d8;
}

/* Premium title styling */
.premium-title .nav-link {
  color: #f8cb00;
}

.premium-title .nav-icon {
  color: #f8cb00;
}

.premium-title .router-link-exact-active {
  color: #f8cb00;
  background: #343b41;
}

.premium-title .router-link-exact-active .nav-icon {
  color: #f8cb00;
}

/* Badge styling */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 16px;
  padding: 0 4px;
  margin-left: auto;
  background: transparent;
  color: #f8cb00;
  font-size: 0;
  font-weight: 700;
  border-radius: 4px;
}

.badge-primary {
  background: transparent;
  color: #f8cb00;
}

/* Blue "New" badge for discovery/new features */
.badge-new {
  background: #20a8d8;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  height: 18px;
}

/* Font Awesome crown icon for premium badges */
.badge.badge-primary::before {
  font-family: "Font Awesome 5 Free";
  content: "\f521"; /* Crown icon */
  font-weight: 900;
  font-size: 14px;
  color: #f8cb00;
}
</style>
